import React, { useCallback, useEffect, useState } from "react";
import MaxWidthContainer from "../../../components/UI/MaxWidthContainer/MaxWidthContainer";
import Page from "../../page-layout";
import { IonIcon, useIonAlert, useIonLoading } from "@ionic/react";
import { useCartData } from "../../../providers/cartDataStore";
import { useConfig } from "../../../providers/config";
import { useGetOrderByIdQuery } from "../../../providers/Orders";
import useReviewSetOrderById from "../../../providers/Orders/useGetReviewSerOrderById";
import useCancelOrderMutation from "../../../providers/Orders/useCancelOrderMutation";
import { useGetIncludeProductsQuery } from "../../../providers/products";
import { useActiveOrMainShippingZone } from "../../../providers/shippingZones";
import { useHistory } from "react-router-dom";
import { chatbubbleEllipses, refresh, wallet, closeCircle } from "ionicons/icons";
import OrderStatus from "../../../parhato2/src/entities/order-status";
import SkeletonMyOrder from "./skeleton-my-order";
import { BackButton } from "../../../parhato2/src/entities";
import RateOrder from "../rate-order";
import PaymentBottomSheet from "./PaymentBottomSheet";
import PullToRefresh from "../../../parhato2/src/entities/pullToRefresh";
import { useTranslation } from "react-i18next";
import Truck from "../../../assets/icon/truck.svg";
import { Image } from "../../../components/Image/Image";
import useOpenProductDetailSheet from "../../../hooks/useOpenProductDetailSheet";
import { LineItem } from "@core/types";
import { AttributeBadge } from "../../../fsd/shared/AttributeBadge/AttributeBadge";
import cln from "classnames";
import OrderTracking from "./OrderTracking";
import { toast } from "react-toastify";
import { calculateTotalAppliedCouponsAndRewardPoints } from "../../../utils/calculateTotalAppliedCoupons";
import { prettifyNumbers } from "../../../utils/prettifyNumbers";
import { useOrderChatQuery, useOrderChatUnreadCount } from "../../../providers/Chat";
import ChatUnreadBadge from "../../../parhato2/src/entities/ChatUnreadBadge";
import { getDeliveryQualityFromReview, getOrderReviewOverallRating } from "../../../utils/getOrderReviewOverallRating";

const MyOrderDetailPage = (props: any) => {
  const { t } = useTranslation();
  const config = useConfig();
  const history = useHistory();
  const { repeatOrder } = useCartData();
  const { activeShippingZone } = useActiveOrMainShippingZone();
  const openProductDetailSheet = useOpenProductDetailSheet();
  const [showPaymentButton, setShowPaymentButton] = useState(false);
  const [isMapInteracting, setIsMapInteracting] = useState(false);
  const [showPaymentForm, setShowPaymentForm] = useState(false);
  const orderId = props.match.params.id;
  const [presentAlert] = useIonAlert();
  const [presentLoading, dismissLoading] = useIonLoading();

  const handleInteractStart = useCallback(() => setIsMapInteracting(true), []);
  const handleInteractEnd = useCallback(() => setIsMapInteracting(false), []);

  const isOrderChatEnabled = !!config.isOrderChatEnabled;
  const {
    data: order,
    isLoading: isLoadingOrderDetail,
    refetch: refetchOrder,
  } = useGetOrderByIdQuery(orderId, {
    refetchOnMount: "always",
    refetchInterval: 60000, // 1 minute
  });

  const {
    mutateAsync: cancelOrderAsync,
    isPending: isCancellingOrder,
    isSuccess: isCancelOrderSuccess,
  } = useCancelOrderMutation();

  const orderReviewSetEnabled = !!order?.id;
  const {
    data: orderReviewSet,
    isLoading: loadingOrderReviewSet,
    isError: isOrderReviewError,
    refetch: refetchOrderReviewSet,
  } = useReviewSetOrderById(String(order?.id), {
    enabled: orderReviewSetEnabled,
  });

  const { data: products } = useGetIncludeProductsQuery({
    params: {
      zoneId: activeShippingZone.id,
    },
    options: {
      enabled: !!order?.lineItems?.length && !!activeShippingZone.id,
    },
    include: order?.lineItems?.map((product) => product.productId + "")!,
  });

  const orderIdNum = Number(order?.id) || undefined;
  const { data: orderChat } = useOrderChatQuery(orderIdNum, { enabled: !!orderIdNum && isOrderChatEnabled });
  const chatUnreadCount = useOrderChatUnreadCount({ orderId: orderIdNum, isOrderChatEnabled });

  const isChatClosed = !!orderChat && orderChat.status !== "open";

  useEffect(() => {
    if (order && config?.isPaymentsEnabled) {
      setShowPaymentButton(order.needsPayment ?? false);
    }
  }, [order, config?.isPaymentsEnabled]);

  const handleGoToProductDetail = (item: LineItem) => {
    if (item.productId) {
      openProductDetailSheet({ id: item.productId });
    }
  };

  const handleRepeatOrder = () => {
    if (!products?.length || !order || !order.lineItems || !order.lineItems.length) {
      return;
    }

    repeatOrder(products, order.lineItems);
    history.push("/cart");
  };

  const handleOpenWhatsApp = () => {
    const cleanedPhone = config.companyPhoneNumber.replace(/\D/g, "");

    const message = `Здравствуйте! Обращаюсь по поводу заказа №${order?.id}.`;
    const url = `https://wa.me/${cleanedPhone}?text=${encodeURIComponent(message)}`;

    window.location.href = url;
  };

  const handleShowPaymentForm = () => {
    setShowPaymentForm(true);
  };

  const handleClosePaymentForm = () => {
    setShowPaymentForm(false);
  };

  const handleCloseAndHidePayment = () => {
    setShowPaymentForm(false);
    setShowPaymentButton(false);
  };

  const handleCancelOrder = () => {
    if (order?.pickerListPrinted) {
      presentAlert({
        header: t("Cannot cancel order"),
        message: t("The order is already being assembled. Would you like to contact our operator via WhatsApp?"),
        buttons: [
          { text: t("Cancel"), role: "cancel" },
          {
            text: t("Contact via WhatsApp"),
            role: "confirm",
            handler: () => {
              handleOpenWhatsApp();
            },
          },
        ],
      });
    } else {
      presentAlert({
        header: t("Confirm Cancel Order"),
        message: t("Are you sure you want to cancel this order?"),
        buttons: [
          { text: t("Cancel"), role: "cancel" },
          {
            text: t("Confirm"),
            role: "confirm",
            handler: async () => {
              if (order?.id) {
                try {
                  await presentLoading();
                  await cancelOrderAsync(String(order.id));
                  refetchOrder();
                  toast.success(t("Order canceled successfully"));
                  if (order.rewardPoints) {
                    toast.info(t("Points will be returned after cancellation"));
                  }
                } catch (error) {
                  console.error(error);
                  toast.error(t("Error canceling order"));
                } finally {
                  await dismissLoading();
                }
              }
            },
          },
        ],
      });
    }
  };

  const isOrderCompleted = order?.status === "completed";
  const orderTotal = order?.total ?? 0;

  const showRateOrderButton =
    orderReviewSetEnabled &&
    !loadingOrderReviewSet &&
    config?.orderDetailPage?.reviewOrderEnabled &&
    !isOrderReviewError &&
    isOrderCompleted &&
    orderReviewSet === null;

  const totalAppliedCouponsAndRewardPoints = order ? calculateTotalAppliedCouponsAndRewardPoints(order) : 0;

  const handleRefetchOrderDetails = () => {
    refetchOrder();
    refetchOrderReviewSet();
  };

  return (
    <Page>
      <Page.Header className="flex flex-col items-center justify-start mt-[env(safe-area-inset-top)] mb-2.5 border-b border-gray-300">
        <MaxWidthContainer classList="flex items-center justify-center bg-white w-full h-16">
          <div className="flex justify-center items-center w-full relative">
            <div className="absolute left-2.5">
              <BackButton />
            </div>
            {/* Use id from url react-router */}
            {orderId && <div className="font-bold">{"№" + orderId}</div>}
          </div>
        </MaxWidthContainer>
      </Page.Header>

      <Page.Content>
        <PullToRefresh disabled={isMapInteracting} />

        <MaxWidthContainer>
          {isLoadingOrderDetail ? (
            <SkeletonMyOrder />
          ) : (
            order && (
              <div className="flex flex-col gap-5 p-2.5 pb-24">
                {/* TODO: Decompose these two children into separate components */}
                <div className="flex flex-col gap-5 w-full shadow-[0_0_20px_rgba(0,0,0,0.2)] rounded-2xl p-5">
                  <div className="flex flex-col justify-center ">
                    <OrderStatus
                      orderStatus={order.status}
                      paymentMethod={order.paymentMethod}
                      orderPaymentStatus={order.paymentStatus}
                      paymentStatus={order.payments?.status ?? ""}
                    />
                  </div>
                  <div className={cln("flex items-center justify-center gap-4")}>
                    {showRateOrderButton && (
                      <RateOrder orderNumber={String(order?.id)} onSuccess={handleRefetchOrderDetails} />
                    )}
                    <PaymentBottomSheet
                      orderId={String(order?.id ?? "")}
                      onClose={handleClosePaymentForm}
                      showPaymentForm={showPaymentForm}
                      onSuccess={handleCloseAndHidePayment}
                    />

                    {config?.orderDetailPage?.cancelOrderEnabled &&
                      (order?.status === "processing" || order?.status === "assembly") && (
                        <button
                          onClick={handleCancelOrder}
                          className="flex flex-col gap-2 items-center text-gray-500 disabled:opacity-50 disabled:cursor-not-allowed"
                          disabled={isCancellingOrder || isCancelOrderSuccess}
                        >
                          <IonIcon icon={closeCircle} className="text-red-500 text-2xl p-3 rounded-full bg-gray-100" />
                          <span className="text-sm font-bold text-red-500">{t("Cancel order")}</span>
                        </button>
                      )}

                    <button onClick={handleRepeatOrder} className="flex flex-col gap-2 items-center text-gray-500">
                      <IonIcon icon={refresh} className="text-[#FCA42A] text-2xl p-3 rounded-full bg-gray-100 " />
                      <span className="text-sm font-bold text-[#FCA42A]">{t("Repeat order")}</span>
                    </button>
                    {showPaymentButton && (
                      <button onClick={handleShowPaymentForm} className="flex flex-col gap-2 items-center ">
                        <IonIcon icon={wallet} className="text-2xl  p-3 rounded-full bg-gray-100 " />
                        <span className="text-sm font-bold ">{t("Pay for the order")}</span>
                      </button>
                    )}
                  </div>
                  <div className="flex gap-3 w-full">
                    <button
                      onClick={handleOpenWhatsApp}
                      className={cln(
                        "relative flex-1 flex items-center justify-center gap-3 py-3.5 px-5 rounded-xl",
                        "bg-green-500 text-white",
                        "shadow-lg shadow-green/25",
                        "transition-all duration-200 active:scale-[0.98]",
                      )}
                    >
                      <IonIcon icon={chatbubbleEllipses} className="text-2xl" />
                      <span className="text-base font-semibold">{t("Contact via WhatsApp")}</span>
                    </button>
                    {config.isOrderChatEnabled && (
                      <button
                        onClick={() => history.push(`/orders/${order.id}/chat`)}
                        disabled={isChatClosed}
                        className={cln(
                          "relative flex-1 flex items-center justify-center gap-3 py-3.5 px-5 rounded-xl",
                          "bg-primary text-white",
                          "shadow-lg shadow-primary/25",
                          "transition-all duration-200 active:scale-[0.98]",
                          "disabled:bg-gray-300 disabled:shadow-none disabled:cursor-not-allowed",
                        )}
                      >
                        <IonIcon icon={chatbubbleEllipses} className="text-2xl" />
                        <span className="text-base font-semibold">{isChatClosed ? t("Chat closed") : t("Chat")}</span>
                        <ChatUnreadBadge
                          count={chatUnreadCount}
                          className="absolute -top-1.5 -right-1.5 border-2 border-white"
                        />
                      </button>
                    )}
                  </div>
                </div>

                {/* // * TODO:: Убрать хардкод для отображения карты и сделать на бэке */}
                {order?.status === "out-for-delivery" && config.orderTrackingEnabled && (
                  <OrderTracking
                    order={order}
                    onInteractStart={handleInteractStart}
                    onInteractEnd={handleInteractEnd}
                  />
                )}
                <div className="w-full bg-white shadow-[0_0_20px_rgba(0,0,0,0.2)] rounded-2xl p-5">
                  <div className="flex flex-col gap-2.5">
                    {Array.isArray(order?.lineItems) &&
                      order.lineItems.map((item) => {
                        const currencySymbol = order?.currencySymbol ?? "₽";

                        return (
                          <div key={item.id} onClick={() => handleGoToProductDetail(item)}>
                            <div className="flex items-center justify-between relative">
                              <div className="flex items-center gap-2.5">
                                <Image
                                  image={item.image}
                                  lazyLoad={false}
                                  className="min-w-16 min-h-16 max-w-16 max-h-16 rounded-lg"
                                />
                                <div className="flex flex-col w-60">
                                  <span className="overflow-hidden text-ellipsis whitespace-nowrap text-xs">
                                    {item.name}
                                  </span>
                                  <span className="text-gray-500 text-xs">
                                    {item.quantity} {t(item.portion_unit ? item.portion_unit : "PCE")} x {item.price}{" "}
                                    {currencySymbol}
                                  </span>
                                  <div className="flex flex-wrap gap-1.5">
                                    {item.attributes?.map((attr) => (
                                      <div key={attr.id} className="flex items-center content-center gap-1">
                                        <div className="text-xs">{attr.name}:</div>
                                        <AttributeBadge value={attr.option ?? ""} isColorPrimary />
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              </div>
                              <div className="text-xs font-semibold self-end absolute right-2.5 bottom-1">
                                {item.total} {currencySymbol}
                              </div>
                            </div>
                          </div>
                        );
                      })}

                    <hr />

                    <div className="flex flex-col gap-2.5">
                      {Number(order?.rewardPoints) > 0 && (
                        <>
                          <div className="flex justify-between text-sm">
                            <span>{t("Points")}</span>
                            <span className="text-primary">{order?.rewardPoints}</span>
                          </div>
                          <hr />
                        </>
                      )}
                      <div className="flex flex-col justify-center py-4">
                        <span className="text-center text-xs text-gray-400">{t("Total")}</span>
                        <div className="flex flex-col justify-center gap-3 text-lg font-semibold">
                          <div className="flex justify-center gap-2">
                            <span className="text-gray-500 line-through">
                              {totalAppliedCouponsAndRewardPoints !== orderTotal && prettifyNumbers(orderTotal) + "₽"}
                            </span>
                            <span className="text-xl">{prettifyNumbers(totalAppliedCouponsAndRewardPoints)} ₽</span>
                            {!!(parseInt(order.shippingLine?.total ?? "0") > 0) && (
                              <div className="flex items-center text-sm">
                                <span className="flex items-center gap-1 relative text-primary">
                                  {"(" + order.shippingTotal + " ₽"}
                                  <IonIcon icon={Truck} className="w-4 h-4" />
                                  {")"}
                                </span>
                                {order.isExpressDelivery && <span className="text-sm">⚡</span>}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="flex flex-col gap-2 text-sm pt-4">
                      {!loadingOrderReviewSet && orderReviewSet !== null && (
                        <>
                          <hr className="text-gray-500" />
                          <div className="flex flex-col gap-1">
                            <span className="text-[0.75rem] text-gray-500">
                              {t("Overall rating")}: {getOrderReviewOverallRating(orderReviewSet!)} {t("out of 5")}
                            </span>
                            <span className="text-sm">
                              <div>{orderReviewSet?.customerNote ?? t("No comment")}</div>
                            </span>
                          </div>
                        </>
                      )}
                      <hr />
                      <div className="flex flex-col">
                        <span className=" text-[0.75rem] text-gray-500">{t("Orders ID")}</span>
                        <span className="text-[0.75rem]">{order?.id}</span>
                      </div>
                      <hr className="text-[0.75rem] text-gray-500" />
                      <div className="flex flex-col">
                        <span className="text-[0.75rem] text-gray-500">{t("Delivery date and interval")}</span>
                        <span className="text-[0.75rem]">
                          {order?.deliveryDate}, {order.isExpressDelivery ? t("Express") : order?.deliveryTime}
                        </span>
                      </div>
                      <hr />
                      <div className="flex flex-col">
                        <span className="text-[0.75rem] text-gray-500">{t("Address")}</span>
                        <span className="text-[0.75rem]">{`${order?.address?.street}, ${order?.address?.house}`}</span>
                      </div>
                      <hr className="text-gray-500" />
                      <div className="flex flex-col">
                        <span className="text-[0.75rem] text-gray-500">{t("Shipping Method")}</span>
                        <span className="text-[0.75rem]">{order?.shippingLine?.methodTitle}</span>
                      </div>
                      <hr />
                      <div className="flex flex-col">
                        <span className="text-[0.75rem] text-gray-500">{t("Payment Method")}</span>
                        <span className="text-[0.75rem]">{order?.paymentMethod?.title}</span>
                      </div>
                      <hr className="text-gray-500" />
                      <div className="flex flex-col">
                        <span className="text-[0.75rem] text-gray-500">{t("Comment")}</span>
                        <span className="text-[0.75rem]">{order?.customerNote}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )
          )}
        </MaxWidthContainer>
      </Page.Content>
    </Page>
  );
};

export default MyOrderDetailPage;
